import { Feather } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import { useParams } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { SummaryLengthBadge } from "@/components/Jobs/SummaryLengthBadge";
import { FeedbackControls } from "@/components/Feedback/FeedbackControls";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext";
import { FEEDBACK_FORM_URL } from "@/shared/constants";

interface SummaryCardProps {
  paragraphs: string[];
  desiredWordCount: number;
}

export function SummaryCard({
  paragraphs,
  desiredWordCount,
}: SummaryCardProps) {
  const { t } = useTranslation("jobDetails");
  const { jobId } = useParams({ from: "/jobs/$jobId", strict: true });
  const { features } = useFeatureFlags();

  return (
    <div className="bg-white rounded-[1.5rem] shadow-base p-6">
      <div className="max-w-[920px] mx-auto">
        <div className="flex items-center gap-2 h-7 mb-6">
          <Feather className="w-[1.125rem] h-[1.125rem] text-foreground" />
          <div className="[&_.badge]:!bg-primary-500 [&_.badge]:!text-white [&_.badge]:!shadow-base">
            <SummaryLengthBadge desiredWordCount={desiredWordCount} />
          </div>
        </div>

        {!features.inAppFeedback && (
          <p
            className="text-sm text-slate-400 mb-4"
            data-testid="feedback-text"
          >
            <Trans
              t={t}
              i18nKey="summary.feedback"
              components={{
                1: (
                  <a
                    href={FEEDBACK_FORM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-500 underline font-medium"
                    data-testid="feedback-link"
                  />
                ),
              }}
            />
          </p>
        )}

        {paragraphs.map((paragraph, index) => (
          <div
            key={`paragraph-${index}`}
            data-testid={`summary-paragraph-${index}`}
            className={cn("group py-5", index === 0 && "pt-0")}
          >
            <p className="summary-text whitespace-pre-wrap select-text">
              {paragraph}
            </p>
            {features.inAppFeedback && (
              <div className="mt-1">
                <FeedbackControls
                  jobId={jobId}
                  contentType="summary"
                  promptKey="feedback.summary.prompt"
                  sectionIndex={index}
                  showExpandedBackground
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
